import React, { useState } from 'react';
import { X } from 'lucide-react';
import { createProject } from '../services/api';

const ProjectCreationModal = ({ isOpen, onClose, user, onProjectCreated }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !user) return;

    setIsSubmitting(true);
    setError('');
    
    try {
      const projectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        owner_id: user.id
      };

      const newProject = await createProject(projectData);
      
      setFormData({ name: '', description: '' });
      onProjectCreated?.(newProject);
      onClose();
    } catch (error) {
      console.error('Failed to create project:', error);
      
      // Handle specific error codes
      if (error.response?.data?.code === 'USER_NOT_SYNCED' || 
          error.response?.data?.code === 'FOREIGN_KEY_CONSTRAINT') {
        setError('Your user profile needs to be synchronized. Please refresh the page and try again.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to create project. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Create New Project</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleCreateProject} className="space-y-4">
          <div>
            <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 mb-1">
              Project Name *
            </label>
            <input
              type="text"
              id="project-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-celadon-300 focus:border-celadon-400"
              placeholder="e.g., Email Assistant Prompts"
              autoFocus
              required
            />
          </div>
          
          <div>
            <label htmlFor="project-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              id="project-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-celadon-300 focus:border-celadon-400 resize-none"
              rows={3}
              placeholder="Describe your project..."
            />
          </div>
          
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={!formData.name.trim() || isSubmitting}
              className="flex-1 px-4 py-2 bg-celadon-600 text-white font-medium rounded-md hover:bg-celadon-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectCreationModal; 